import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Globals } from 'app/_globals';
import { AssetScheduleAddWorkflowItem } from 'app/_models/workflow-items/asset-schedule-add-workflow-item';
import { AssetScheduleDeleteWorkflowItem } from 'app/_models/workflow-items/asset-schedule-delete-workflow-item';
import { AssetAvailabilityScheduleService } from 'app/_services/asset-availability-schedule.service';
import { NotificationService } from 'app/_services/notification.service';
import { SiteService } from 'app/_services/site.service';
import { WorkflowItemService } from 'app/_services/workflow-items.service';
import { AddAvailabilityModalComponent } from 'app/shared-pages/site-pages/site-assets/asset-page/add-availability-modal/add-availability-modal.component';
import { ApproveAvailabilityModalComponent } from 'app/shared-pages/site-pages/site-assets/asset-page/approve-availabilty-modal/approve-availabilty-modal.component';
import { ApproveCancelledAvailabilityModalComponent } from 'app/shared-pages/site-pages/site-assets/asset-page/approve-cancelled-availabilty-modal/approve-cancelled-availabilty-modal.component';
import { EditAvailabilityModalComponent } from 'app/shared-pages/site-pages/site-assets/asset-page/edit-availability-modal/edit-availability-modal.component';
import { combineLatest } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { ViewPendingScheduleItemsModal } from '../components/view-pending-schedule-items-modal/view-pending-schedule-items-modal.component';

@Component({
  selector: 'site-schedule',
  templateUrl: './site-schedule.component.html'
})
export class SiteScheduleComponent implements OnInit {
  private selectedSiteId: any;
  public response: any;

  public filters: { key: string; value: string; }[];
  public startTime: Date;
  public endTime: Date;
  public hoursPerColumn = 1;
  public selectedSite: any;
  public initialDate: Date = new Date();
  private routeRole: any;
  pendingItems: AssetScheduleAddWorkflowItem[] = [];
  cancelledItems: any[] = [];

  constructor(
    private siteService: SiteService,
    private workflowItemService: WorkflowItemService,
    private notificationService: NotificationService,
    private assetAvailabilityScheduleService: AssetAvailabilityScheduleService,
    private modalService: NgbModal,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    globals: Globals,
  ) {
    this.routeRole = globals.user.routeRole;
  }

  ngOnInit() {
    combineLatest(this.activatedRoute.params, this.activatedRoute.queryParams)
    .pipe(map(results => {
      return ({
        selectedSiteId: results[0].id,
        initialDate: results[1].date
      });
    }), debounceTime(0))
    .subscribe(results => {
      this.selectedSiteId = results.selectedSiteId;

      this.setDateToTriggerFetchResults(results.initialDate ? new Date(results.initialDate) : new Date());
      this.fetchSiteDetails();
    });
  }

  private fetchSiteDetails() {
    this.siteService.getSiteById(this.selectedSiteId).subscribe((data: any) => {
      this.selectedSite = data;
      const allowAssetScheduling = data.siteOwner.siteOwnerUser.allowAssetScheduling;

      if (!allowAssetScheduling) {
        this.router.navigate(['/' + this.routeRole + '/sites/', this.selectedSiteId]);
      }
    });
  }

  // the child component filter triggers fetch result when the date changes
  // allowing it to handle all updates prevents duplicate http calls
  public setDateToTriggerFetchResults(date: Date) {
    this.initialDate = date;
  }

  public fetchResults() {
    this.response = null;

    const pagination = {
      page: 0,
      size: 1000,
      totalRecords: 0,
    };

    this.workflowItemService.getWorkflowItemsByCategory('asset-booking', 'PENDING', pagination).subscribe((workflowResponse) => {
      const items = workflowResponse.body || [];
      this.getSiteAvailabilityForCurrentFilters().subscribe(data => {
        data.assets.forEach(asset => {
          asset.assetAvailability.forEach(booking => {
            booking.displayOrder = 0;
          });
        });

        this.pendingItems = (items
          .filter(item => item.workflowType === 'ASSET_BOOKING_REQUEST')) as any as AssetScheduleAddWorkflowItem[];
        this.cancelledItems = (items
          .filter(item => item.workflowType === 'ASSET_BOOKING_CANCELLATION')) as any as AssetScheduleDeleteWorkflowItem[];

        for (const item of this.pendingItems) {
          const matchingAsset = data.assets.find((asset: any) => asset.id === (item as AssetScheduleAddWorkflowItem).asset.id);

          if (matchingAsset) {
            if (!matchingAsset.assetAvailability) {
              matchingAsset.assetAvailability = [];
            }

            const mappedItem = {
              id: item.id,
              startTime: item.startTime,
              endTime: item.endTime,
              bazefieldUpdatedTime: null,
              workOrderId: null,
              reason: item.reason,
              deleted: false,
              status: 'PENDING',
              workflowType: item.workflowType,
              workflowId: item.id,
              createdDate: item.createdDate,
              createdBy: item.createdBy,
              displayOrder: 0,
            };

            matchingAsset.assetAvailability.push(mappedItem);
          }
        }

        data.assets.forEach(asset => {
          asset.assetAvailability.forEach(aa => {
            const cancelledItem = this.cancelledItems.find(cancelledItem => cancelledItem.assetAvailabilityId === aa.id);

            if (cancelledItem) {
              aa.status = 'CANCELLED';
              aa.workflowType = cancelledItem.workflowType;
              aa.cancelledItem = cancelledItem;
            }
          });
        });

        data.assets.forEach(asset => {
          this.findAndMarkOverlappingGroups(asset.assetAvailability);
        });

        this.response = data;
      });
    }, error => {
      console.log(error);
      return this.notificationService.notify('danger', 'Error fetching approval requests', 'Please try again later');
    });
  }

  private getSiteAvailabilityForCurrentFilters() {
    return this.assetAvailabilityScheduleService.getAllAvailabilityItemsBySite(this.selectedSiteId, this.filters);
  }

  private findAndMarkOverlappingGroups(items: any[]): void {
    const groups: any[][] = [];

    for (let i = 0; i < items.length; i++) {
      const currentGroup: any[] = [items[i]];
      for (let j = 0; j < items.length; j++) {
        if (i !== j && this.isOverlap(items[i], items[j])) {
          currentGroup.push(items[j]);
        }
      }

      if (currentGroup.length > 1) {
        groups.push(currentGroup);
      }
    }

    groups.forEach((group, index) => {
      group.forEach((item, innerIndex) => {
        item.displayOrder = innerIndex + 1;
      });
    });
  }

  private isOverlap(item1: any, item2: any): boolean {
    return (
      (item1.startTime <= item2.startTime && item1.endTime > item2.startTime) ||
      (item1.startTime < item2.endTime && item1.endTime >= item2.endTime) ||
      (item2.startTime <= item1.startTime && item2.endTime > item1.startTime) ||
      (item2.startTime < item1.endTime && item2.endTime >= item1.endTime)
    );
  }

  public onFiltersChanged(event) {
    this.filters = event.filters;
    this.hoursPerColumn = event.hoursPerColumn;
    this.startTime = event.startTime;
    this.endTime = event.endTime;
    this.fetchResults();
  }

  onGridClick(event) {
    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static' };
    const modal = this.modalService.open(AddAvailabilityModalComponent, modalOptions);
    modal.componentInstance.assetId = event.assetId;
    modal.componentInstance.startDate = event.date;

    const endDate = new Date(event.date);
    endDate.setHours(endDate.getHours() + this.hoursPerColumn);
    modal.componentInstance.endDate = endDate;

    modal.result.then(result => {
      if (result === 'refresh') {
        this.fetchResults();
      }
    }).catch(() => { /* modal dismissed */ });
  }

  onBookingClick(event) {
    if (event.booking.workflowType === 'ASSET_BOOKING_REQUEST') {
      const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static' };
      const modal = this.modalService.open(ApproveAvailabilityModalComponent, modalOptions);
      modal.componentInstance.scheduleItem = event.booking;
      modal.componentInstance.assetId = event.assetId;

      modal.result.then(result => {
        if (result === 'refresh') {
          this.fetchResults();
        }
      }).catch(() => { /* modal dismissed */ });
    } else if (event.booking.workflowType === 'ASSET_BOOKING_CANCELLATION') {
      const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static' };
      const modal = this.modalService.open(ApproveCancelledAvailabilityModalComponent, modalOptions);
      modal.componentInstance.scheduleItem = event.booking;
      modal.componentInstance.assetId = event.assetId;

      modal.result.then(result => {
        if (result === 'refresh') {
          this.fetchResults();
        }
      }).catch(() => { /* modal dismissed */ });
    } else {
      const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static' };
      const modal = this.modalService.open(EditAvailabilityModalComponent, modalOptions);
      modal.componentInstance.scheduleItem = event.booking;
      modal.componentInstance.assetId = event.assetId;

      modal.result.then(result => {
        if (result === 'refresh') {
          this.fetchResults();
        }
      }).catch(() => { /* modal dismissed */ });
    }
  }

  public viewPendingList() {
    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static', size: 'lg' };
    const modal = this.modalService.open(ViewPendingScheduleItemsModal, modalOptions);
    modal.componentInstance.pendingItems = this.pendingItems;
    modal.componentInstance.cancelledItems = this.cancelledItems;

    modal.result.then(booking => {
      if (booking) {
        let event = { booking };

        if (booking.workflowType === 'ASSET_BOOKING_CANCELLATION') {
          event.booking.cancelledItem = booking;
        }

        if (this.selectedSiteId !== String(event.booking.site.id)) {
          this.router.navigate(['/' + this.routeRole + '/sites/' + event.booking.site.id + '/schedule/'], { queryParams: { workflowId: event.booking.id, date: event.booking.getStartDateWithoutTime() }});
          this.onBookingClick(event);
        } else if (this.initialDate.toISOString() !== new Date(event.booking.getStartDateWithoutTime()).toISOString()) {
          this.setDateToTriggerFetchResults(new Date(event.booking.getStartDateWithoutTime()));
          this.onBookingClick(event);
        } else {
          this.onBookingClick(event);
        }
      }
    }).catch(() => { /* modal dismissed */ });
  }
}
